import * as R from 'remeda';
import { Location } from 'history';
import { NavLinkProps } from 'react-router-dom';
import { isDevFeatureEnabled } from '../../constants/dev';
import { activitiesPath } from '../../containers/Activities/routes';
import {
  automationEnginesPath,
  automationPath,
} from '../../containers/Automation/helpers';
import { configurationChangelogPath } from '../../features/configuration-changelog/routes';
import { datasetsPath } from '../../features/datasets/routes';
import { dashboardPath } from '../../features/document-list/routes';
import { credentialsPath } from '../../features/integration-credentials/core';
import { workflowsSettingsPath } from '../../features/workflows/settings/routes';
import { parse, stringify } from '../../lib/url';
import { STATISTICS_QUERY } from '../../redux/modules/localStorage/actions';
import { GroupRole } from '../../types/group';

type VisibleMenuItemsForTabContext = {
  features: {
    isLabelsEnabled?: boolean;
    isDatasetsEnabled?: boolean;
    isRuleManagerEnabled: boolean;
    isCredentialsEnabled: boolean;
    isExternalStoresEnabled: boolean;
    isWorkflowsEnabled?: boolean;
  };
};

export type MenuItemDefinition<
  Name extends string,
  ParentName extends string,
> = {
  name: Name;
  link: string;
  isVisible?: (context: VisibleMenuItemsForTabContext) => boolean;
  roles?: GroupRole[];
  dataCy: string;
  translationKey: `${ParentName}.${Name}`;
  isActive?: NavLinkProps['isActive'];
  beta?: boolean;
  deprecated?: boolean;
};

export type LinkContext = { shouldDisplayNewDashboard?: boolean };

type TabDefinition<
  MName extends string,
  Name extends string,
  MenuItems extends Array<MenuItemDefinition<MName, Name>>,
> = {
  name: Name;
  roles?: GroupRole[];
  menu?: MenuItems;
  link: string | ((context?: LinkContext) => string);
  isSelected?: (location: Location<unknown>) => boolean;
};

// Helper functions that are used only to infer correct types
const tabItem = <
  MN extends string,
  N extends string,
  // WORKAROUND: [] is used instead of undefined until we have strict mode turned on
  M extends Array<MenuItemDefinition<MN, N>> = [],
>(
  t: TabDefinition<MN, N, M>
) => t;
// `const` type params keep `name`/`translationKey` inferred as exact literals
// (TS 6.0 otherwise widens them to `${string}` through the `tabItem` generics).
const menuItem = <const N extends string, const P extends string>(
  m: MenuItemDefinition<N, P>
) => m;

export const settingsMenuItems = [
  menuItem({
    name: 'queueManager',
    link: '/settings/queue-manager',
    dataCy: 'settings-queue-manager-link',
    translationKey: 'settings.queueManager',
    roles: ['admin', 'organization_group_admin'],
  }),
  menuItem({
    name: 'users',
    link: '/settings/users',
    dataCy: 'settings-users-link',
    translationKey: 'settings.users',
    roles: ['admin', 'organization_group_admin'],
  }),
  menuItem({
    name: 'fieldManager',
    link: '/settings/field-manager',
    dataCy: 'settings-field-manager-link',
    translationKey: 'settings.fieldManager',
    roles: ['admin', 'organization_group_admin'],
  }),
  menuItem({
    name: 'labels',
    link: '/settings/labels',
    isVisible: ({ features }) => !!features.isLabelsEnabled,
    dataCy: 'settings-labels-link',
    translationKey: 'settings.labels',
    roles: ['admin', 'organization_group_admin', 'empowered_manager'],
  }),
  menuItem({
    name: 'datasets',
    link: datasetsPath(),
    dataCy: 'settings-datasets-link',
    translationKey: 'settings.datasets',
    isVisible: ({ features }) => !!features.isDatasetsEnabled,
    roles: ['admin', 'organization_group_admin'],
  }),
  menuItem({
    name: 'ruleManager',
    link: '/settings/rule-manager',
    isVisible: ({ features }) => features.isRuleManagerEnabled,
    dataCy: 'settings-rule-manager-link',
    translationKey: 'settings.ruleManager',
    beta: true,
    roles: ['admin', 'organization_group_admin'],
  }),
  menuItem({
    name: 'activities',
    link: activitiesPath(),
    dataCy: 'settings-activities-link',
    translationKey: 'settings.activities',
    roles: ['admin', 'organization_group_admin'],
  }),
  menuItem({
    name: 'configurationChangelog',
    link: configurationChangelogPath(),
    dataCy: 'settings-configuration-changelog-link',
    translationKey: 'settings.configurationChangelog',
    roles: ['admin', 'organization_group_admin'],
  }),
  menuItem({
    name: 'workflows',
    link: workflowsSettingsPath(),
    dataCy: 'settings-workflows-link',
    translationKey: 'settings.workflows',
    isVisible: ({ features }) => !!features.isWorkflowsEnabled,
    roles: ['admin', 'organization_group_admin'],
  }),
  menuItem({
    name: 'credentials',
    link: credentialsPath(),
    dataCy: 'settings-credentials-link',
    translationKey: 'settings.credentials',
    isVisible: ({ features }) => features.isCredentialsEnabled,
    roles: ['admin', 'organization_group_admin'],
  }),
  menuItem({
    name: 'appearance',
    link: '/settings/appearance',
    dataCy: 'settings-appearance-link',
    translationKey: 'settings.appearance',
    isVisible: () => !!isDevFeatureEnabled('appearance'),
    roles: ['admin', 'organization_group_admin'],
  }),
];

export const tabs = [
  tabItem({
    name: 'documents',
    link: () => dashboardPath(),
    isSelected: location =>
      Boolean(location.pathname.match(/^\/annotations|^\/emails|^\/documents/)),
  }),
  tabItem({
    name: 'requests',
    link: '/requests',
    isSelected: location => Boolean(location.pathname.match(/^\/requests/)),
  }),
  tabItem({
    name: 'automation',
    link: automationPath(),
    roles: ['admin', 'organization_group_admin'],
    menu: [
      menuItem({
        name: 'automationSettings',
        link: automationPath(),
        dataCy: 'settings-sidebar-automation-link',
        translationKey: 'automation.automationSettings',
      }),
      menuItem({
        name: 'aiEngines',
        link: automationEnginesPath(),
        dataCy: 'settings-sidebar-engines-link',
        translationKey: 'automation.aiEngines',
      }),
    ],
  }),
  tabItem({
    name: 'extensions',
    roles: ['admin', 'organization_group_admin'],
    link: '/extensions/my-extensions',
    menu: [
      menuItem({
        name: 'myExtensions',
        link: '/extensions/my-extensions',
        dataCy: 'settings-sidebar-extensions-link',
        translationKey: 'extensions.myExtensions',
      }),
      menuItem({
        name: 'rossumStore',
        link: '/extensions/store',
        dataCy: 'settings-sidebar-store-link',
        translationKey: 'extensions.rossumStore',
      }),
      menuItem({
        name: 'externalStore',
        link: '/extensions/external-store',
        dataCy: 'settings-sidebar-custom-store',
        translationKey: 'extensions.externalStore',
        isVisible: ({ features }) => !!features.isExternalStoresEnabled,
      }),
      menuItem({
        name: 'activities',
        link: '/extensions/activities',
        dataCy: 'settings-sidebar-extensions-activities',
        translationKey: 'extensions.activities',
      }),
    ],
  }),
  tabItem({
    name: 'reports',
    roles: [
      'admin',
      'organization_group_admin',
      'manager',
      'empowered_manager',
    ],
    link: '/reporting',
  }),
  tabItem({
    name: 'statistics',
    roles: [
      'admin',
      'organization_group_admin',
      'manager',
      'empowered_manager',
    ],
    link: () => {
      const items = parse(localStorage.getItem(STATISTICS_QUERY) || '');

      const validItems = R.pick(items, [
        'beginDate',
        'endDate',
        'groupBy',
        'queues',
        'users',
      ]);

      return !R.isEmpty(validItems)
        ? `/statistics?${stringify(validItems)}`
        : '/statistics';
    },
  }),
  tabItem({
    name: 'settings',
    roles: ['admin', 'organization_group_admin', 'empowered_manager'],
    link: '/settings',
    menu: settingsMenuItems,
  }),
];

export type Tab = (typeof tabs)[number];
export type TabName = (typeof tabs)[number]['name'];
export type MenuItem = Extract<
  (typeof tabs)[number]['menu'],
  Array<unknown>
>[number];

const tabByName = <N extends TabName>(tabName: N): Extract<Tab, { name: N }> =>
  tabs.find(t => t.name === tabName) as Extract<Tab, { name: N }>;

export const tabToLink = (
  tab: { link: Tab['link'] },
  linkContext?: LinkContext
) => (typeof tab.link === 'string' ? tab.link : tab.link(linkContext));

export const isTabVisible = (tab: Tab, role: GroupRole) =>
  tab.roles === undefined || tab.roles.includes(role);

export const visibleMenuItemsForTab = (
  menuItems: MenuItem[],
  context: VisibleMenuItemsForTabContext,
  role: GroupRole
) =>
  menuItems.filter(
    m =>
      (m.roles === undefined || m.roles.includes(role)) &&
      (m.isVisible === undefined || m.isVisible(context))
  );

export const stripQuery = (url: string) => {
  const startOfQuery = url.indexOf('?');

  return startOfQuery === -1 ? url : url.slice(0, startOfQuery);
};

export const isTabSelected = (tab: Tab, location: Location<unknown>) => {
  if (tab.isSelected) {
    // Custom selection logic has priority
    return tab.isSelected(location);
  }

  // Check if the tab or any of the menu items referes to the current path.
  // We ignore query part of the URL, since it usually refers to last parameters used on the target page,
  // and it shouldn't be part of the decision.
  if (location.pathname.startsWith(stripQuery(tabToLink(tab)))) {
    return true;
  }

  if (tab.menu) {
    return tab.menu.some(m => location.pathname.startsWith(stripQuery(m.link)));
  }

  return false;
};

export const defaultTab = tabByName('documents');
